import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaBars, FaTimes, FaUserPlus, FaSignInAlt, FaShieldAlt, FaSun, FaMoon } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

const Navbar = () => {
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [scrolled, setScrolled] = useState(false);
	const { user, logout } = useAuth();
	const { isDark, toggleTheme } = useTheme();

	useEffect(() => {
		const handleScroll = () => {
			setScrolled(window.scrollY > 60);
		};
		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	const navItems = [
		{ name: 'Home', href: '/#home' },
		{ name: 'About', href: '/#about' },
		{ name: 'Courses', href: '/#courses' },
		{ name: 'Blog', href: '/blog/' },
		{ name: 'Gallery', href: '/gallery/' },
		{ name: 'Contact', href: '/#contact' }
	];

	return (
		<nav
			className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isMenuOpen
					? 'bg-white dark:bg-maroon-dark shadow-xl py-2'
					: scrolled
					? 'bg-white/95 dark:bg-maroon-dark/95 backdrop-blur-md shadow-xl py-2'
					: 'bg-transparent py-4'
				}`}
		>
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
				<div className="flex justify-between items-center">
					{/* Logo */}
					<Link to="/" className="flex items-center space-x-3 group">
						<div className="w-10 h-10 rounded-full overflow-hidden shadow-lg border-2 border-gold/40 group-hover:border-gold transition-all duration-300">
							<img src="/images/logo.jpg" alt="Taalsetu Logo" className="w-full h-full object-cover" />
						</div>
						<span className={`text-2xl font-serif font-bold transition-all duration-500 ${scrolled || isMenuOpen
								? 'text-maroon dark:text-gold-light'
								: 'text-white'
							}`}>
							Taalsetu
						</span>
					</Link>

					{/* Desktop Navigation */}
					<nav className="hidden lg:flex items-center space-x-1">
						{navItems.map((item) => (
							<Link
								key={item.name}
								to={item.href}
								className={`font-sans font-medium px-5 py-2 rounded-lg transition-all duration-300 relative group ${scrolled
										? 'text-maroon dark:text-gold-light font-bold hover:text-gray-800 dark:hover:text-white'
										: 'text-gold hover:text-yellow-400'
									}`}
							>
								{item.name}
								<span className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 group-hover:w-3/4 transition-all duration-300 rounded-full ${scrolled ? 'bg-maroon dark:bg-gold' : 'bg-gold'}`}></span>
							</Link>
						))}
						{user && user.role !== 'admin' && (
							<Link
								to="/lessons"
								className={`font-sans font-medium px-5 py-2 rounded-lg transition-all duration-300 relative group ${scrolled
										? 'text-maroon dark:text-gold-light font-bold hover:text-gray-800 dark:hover:text-white'
										: 'text-gold hover:text-yellow-400'
									}`}
							>
								Lessons
								<span className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 group-hover:w-3/4 transition-all duration-300 rounded-full ${scrolled ? 'bg-maroon dark:bg-gold' : 'bg-gold'}`}></span>
							</Link>
						)}
					</nav>

					{/* Auth Buttons + Theme Toggle - Desktop */}
					<div className="hidden md:flex items-center space-x-3">
						{/* Theme Toggle */}
						<button
							onClick={toggleTheme}
							className={`relative w-10 h-10 rounded-full flex items-center justify-center transition-all duration-500 group ${scrolled || isMenuOpen
									? 'text-maroon dark:text-gold hover:bg-maroon/10 dark:hover:bg-gold/10'
									: 'text-gold hover:bg-gold/10'
								}`}
							aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
							title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
						>
							<div className="relative w-5 h-5">
								<FaSun className={`absolute inset-0 w-5 h-5 transition-all duration-500 ${isDark ? 'opacity-0 rotate-90 scale-0' : 'opacity-100 rotate-0 scale-100'}`} />
								<FaMoon className={`absolute inset-0 w-5 h-5 transition-all duration-500 ${isDark ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-0'}`} />
							</div>
						</button>

						{user?.role === 'admin' && (
							<Link
								to="/admin/dashboard"
								className="flex items-center px-5 py-2 text-gold border border-gold/40 rounded-full hover:bg-gold/10 transition-all duration-300 text-sm font-medium"
							>
								<FaShieldAlt className="mr-2 text-xs" />
								Admin
							</Link>
						)}
						{!user ? (
							<>
								<Link
									to="/login"
									className={`flex items-center px-5 py-2 rounded-full border transition-all duration-300 text-sm font-medium ${scrolled
											? 'text-maroon dark:text-white border-maroon/30 dark:border-white/30 hover:bg-maroon/10 dark:hover:bg-white/10'
											: 'text-white border-white/30 hover:bg-white/10'
										}`}
								>
									<FaSignInAlt className="mr-2 text-xs" />
									Login
								</Link>
								<Link
									to="/register"
									className="btn-gold flex items-center !py-2 !px-5 !text-sm"
								>
									<FaUserPlus className="mr-2 text-xs" />
									Join Now
								</Link>
							</>
						) : (
							<button
								onClick={logout}
								className="flex items-center px-5 py-2 text-red-300 border border-red-400/30 rounded-full hover:bg-red-500/10 transition-all duration-300 text-sm font-medium"
							>
								<FaSignInAlt className="mr-2 text-xs" />
								Logout
							</button>
						)}
					</div>

					{/* Mobile: Theme Toggle + Menu Button */}
					<div className="lg:hidden flex items-center space-x-2">
						{/* Mobile Theme Toggle */}
						<button
							onClick={toggleTheme}
							className={`p-2 rounded-lg transition-colors duration-300 ${scrolled || isMenuOpen
									? 'text-maroon dark:text-gold hover:text-gray-800 dark:hover:text-gold-light'
									: 'text-gold hover:text-yellow-400'
								}`}
							aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
						>
							{isDark ? <FaSun size={20} /> : <FaMoon size={20} />}
						</button>

						<button
							onClick={() => setIsMenuOpen(!isMenuOpen)}
							className={`p-2 rounded-lg transition-colors duration-300 ${scrolled || isMenuOpen
									? 'text-maroon dark:text-gold-light hover:text-gray-800 dark:hover:text-white'
									: 'text-white hover:text-gray-200'
								}`}
						>
							{isMenuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
						</button>
					</div>
				</div>

				{/* Mobile Menu */}
				{isMenuOpen && (
					<div className="lg:hidden border-t border-gold/20 mt-4 pt-4 pb-4">
						<nav className="flex flex-col space-y-1 mb-4">
							{navItems.map((item) => (
								<Link
									key={item.name}
									to={item.href}
									onClick={() => setIsMenuOpen(false)}
									className="font-sans font-medium py-3 px-4 rounded-lg transition-all duration-300 text-maroon dark:text-gold-light font-bold hover:bg-maroon/5 dark:hover:bg-white/5 hover:text-gray-800 dark:hover:text-white"
								>
									{item.name}
								</Link>
							))}
							{user && user.role !== 'admin' && (
								<Link
									to="/lessons"
									onClick={() => setIsMenuOpen(false)}
									className="font-sans font-medium py-3 px-4 rounded-lg transition-all duration-300 text-maroon dark:text-gold-light font-bold hover:bg-maroon/5 dark:hover:bg-white/5 hover:text-gray-800 dark:hover:text-white"
								>
									Lessons
								</Link>
							)}
						</nav>

						<div className="flex flex-col space-y-3 pt-4 border-t border-gold/10">
							{!user ? (
								<>
									<Link
										to="/login"
										onClick={() => setIsMenuOpen(false)}
										className="flex items-center justify-center px-4 py-3 text-maroon dark:text-white border-maroon/30 dark:border-white/30 border rounded-full hover:bg-maroon/10 dark:hover:bg-white/10 transition-all duration-300 font-medium"
									>
										<FaSignInAlt className="mr-2" />
										Login
									</Link>
									<Link
										to="/register"
										onClick={() => setIsMenuOpen(false)}
										className="btn-gold flex items-center justify-center !py-3"
									>
										<FaUserPlus className="mr-2" />
										Join Taalsetu
									</Link>
								</>
							) : (
								<button
									onClick={() => { logout(); setIsMenuOpen(false); }}
									className="flex items-center justify-center px-4 py-3 text-red-300 border border-red-400/30 rounded-full hover:bg-red-500/10 transition-all duration-300 font-medium"
								>
									<FaSignInAlt className="mr-2" />
									Logout
								</button>
							)}
						</div>
					</div>
				)}
			</div>
		</nav>
	);
};

export default Navbar;
